import React, { useEffect, useState } from 'react';
import { supportApi } from '../../services/supportApi';

const CATEGORIES = [
  { value: 'payment_issue', label: 'Vấn đề thanh toán' },
  { value: 'room_pay_qr', label: 'QR / thanh toán phòng' },
  { value: 'slow_data', label: 'Dữ liệu app chậm' },
  { value: 'login_account', label: 'Đăng nhập / tài khoản' },
  { value: 'contract', label: 'Hợp đồng' },
  { value: 'notification', label: 'Thông báo' },
  { value: 'display_crash', label: 'Lỗi hiển thị / crash' },
  { value: 'other', label: 'Vấn đề khác' },
] as const;

type CategoryValue = (typeof CATEGORIES)[number]['value'];

const THANK_YOU =
  'Cảm ơn bạn đã đóng góp ý kiến chúng tôi sẽ cố gắng giải quyết vấn đề nhanh nhất có thể';

const MobileSupportFab: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<CategoryValue | ''>('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [thanksOpen, setThanksOpen] = useState(false);

  useEffect(() => {
    if (!open && !thanksOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setThanksOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, thanksOpen]);

  const resetForm = () => {
    setCategory('');
    setContent('');
    setError('');
    setSubmitting(false);
  };

  const closeForm = () => {
    setOpen(false);
    resetForm();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!category) {
      setError('Vui lòng chọn loại vấn đề');
      return;
    }
    const trimmed = content.trim();
    if (!trimmed) {
      setError('Vui lòng nhập nội dung vấn đề');
      return;
    }

    const selected = CATEGORIES.find((item) => item.value === category);
    const subject = selected?.label || 'Vấn đề khác';

    try {
      setSubmitting(true);
      setError('');
      await supportApi.createRequest({
        request_type: category,
        subject,
        description: trimmed,
        priority: 'normal',
      });
      closeForm();
      setThanksOpen(true);
    } catch (err: any) {
      const message =
        err?.response?.data?.detail
        || err?.message
        || 'Không gửi được ý kiến. Vui lòng thử lại.';
      setError(typeof message === 'string' ? message : 'Không gửi được ý kiến. Vui lòng thử lại.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        title="Hỗ trợ"
        onClick={() => {
          resetForm();
          setOpen(true);
        }}
        className="rounded-full bg-[#2B2520] px-3.5 py-2.5 text-xs font-semibold text-[#E4C078] shadow-[0_8px_18px_rgba(43,37,32,0.35)]"
      >
        Hỗ trợ
      </button>

      {open && (
        <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <button
            type="button"
            className="absolute inset-0 cursor-default"
            aria-label="Đóng hỗ trợ"
            onClick={closeForm}
          />
          <div className="relative z-10 w-full max-w-sm overflow-hidden rounded-2xl border-2 border-[#D8B86A] bg-gradient-to-b from-[#FFFDF8] to-[#F8EFD8] shadow-xl">
            <div className="flex items-center justify-between border-b border-[#D8B86A]/60 bg-[#2B2520] px-4 py-3">
              <h2 className="text-base font-bold text-white">Hỗ trợ</h2>
              <button
                type="button"
                onClick={closeForm}
                className="rounded-xl bg-[#C4A35A] px-3 py-1.5 text-sm font-semibold text-white"
              >
                Đóng
              </button>
            </div>

            <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3 p-3">
              <div>
                <label htmlFor="support-category" className="mb-1 block text-xs font-semibold text-black/70">
                  Loại vấn đề
                </label>
                <select
                  id="support-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as CategoryValue | '')}
                  className="w-full rounded-xl border border-[#D8B86A] bg-white px-3 py-2.5 text-sm text-black outline-none"
                >
                  <option value="">Chọn vấn đề...</option>
                  {CATEGORIES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="support-content" className="mb-1 block text-xs font-semibold text-black/70">
                  Nội dung
                </label>
                <textarea
                  id="support-content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={4}
                  placeholder="Mô tả vấn đề bạn gặp phải..."
                  className="w-full resize-none rounded-xl border border-[#D8B86A] bg-white px-3 py-2.5 text-sm text-black outline-none placeholder:text-black/35"
                />
              </div>

              {error && (
                <p className="rounded-xl bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-xl bg-[#C4A35A] px-3 py-3 text-sm font-semibold text-white disabled:opacity-60"
              >
                {submitting ? 'Đang gửi...' : 'Gửi'}
              </button>
            </form>
          </div>
        </div>
      )}

      {thanksOpen && (
        <div className="fixed inset-0 z-[96] flex items-center justify-center bg-black/40 p-4">
          <button
            type="button"
            className="absolute inset-0 cursor-default"
            aria-label="Đóng thông báo"
            onClick={() => setThanksOpen(false)}
          />
          <div className="relative z-10 w-full max-w-sm rounded-2xl border-2 border-[#D8B86A] bg-white p-5 text-center shadow-xl">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#C4A35A]/15 text-[#C4A35A]">
              <i className="fas fa-check text-lg" />
            </div>
            <p className="text-sm font-medium leading-relaxed text-black">{THANK_YOU}</p>
            <button
              type="button"
              onClick={() => setThanksOpen(false)}
              className="mt-4 w-full rounded-xl bg-[#C4A35A] px-3 py-2.5 text-sm font-semibold text-white"
            >
              Đóng
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileSupportFab;
